import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'siteImages'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

interface SiteImagesState {
	entities: Record<string, any>
	/** Ids for the site currently loaded into `entities`, newest first. */
	ids: string[]
	/** The site `ids` belongs to, so a stale list is never shown for another. */
	loadedSiteId: string | null
	isLoading: boolean
	error: string | null
}

const initialState: SiteImagesState = {
	entities: {},
	ids: [],
	loadedSiteId: null,
	isLoading: false,
	error: null,
}

export const getSiteImages = createAsyncThunk(
	`${SLICE_NAME}/get`,
	async (siteId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/siteimages/images', {
				params: { siteid: siteId },
				...prepConfig(getState),
			})
			return { siteId, images: data }
		} catch {
			return rejectWithValue('Failed to load site images')
		}
	},
)

export const createSiteImage = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (image: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/siteimages', image, prepConfig(getState))
			return data
		} catch {
			return rejectWithValue('Failed to save site image')
		}
	},
)

export const updateSiteImage = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (image: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(
				`/api/siteimages/${image._id}`,
				image,
				prepConfig(getState),
			)
			return data
		} catch {
			return rejectWithValue('Failed to update site image')
		}
	},
)

export const deleteSiteImage = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (imageId: string, { getState, rejectWithValue }) => {
		try {
			await axios.put(`/api/siteimages/${imageId}/delete`, {}, prepConfig(getState))
			return imageId
		} catch {
			return rejectWithValue('Failed to remove site image')
		}
	},
)

const siteImageSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder
			.addCase(getSiteImages.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(getSiteImages.fulfilled, (state, action) => {
				state.isLoading = false
				state.entities = {}
				state.ids = []
				action.payload.images.forEach((image: any) => {
					state.entities[image._id] = image
					state.ids.push(image._id)
				})
				state.loadedSiteId = action.payload.siteId
			})
			.addCase(getSiteImages.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})
			// Note the `ids` push. locationImageSlice writes entities only, which
			// is why a new image there stays invisible until a refetch.
			.addCase(createSiteImage.fulfilled, (state, action) => {
				const image = action.payload
				state.entities[image._id] = image
				if (!state.ids.includes(image._id)) state.ids.unshift(image._id)
			})
			.addCase(createSiteImage.rejected, (state, action) => {
				state.error = action.payload as string
			})
			.addCase(updateSiteImage.fulfilled, (state, action) => {
				const image = action.payload
				state.entities[image._id] = image
				if (!state.ids.includes(image._id)) state.ids.unshift(image._id)
			})
			.addCase(deleteSiteImage.fulfilled, (state, action) => {
				delete state.entities[action.payload]
				state.ids = state.ids.filter(id => id !== action.payload)
			})
	},
})

export default siteImageSlice
